import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import MobileLayout from "@/components/MobileLayout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import StoreVisits from "@/pages/StoreVisits";
import StoreVisitForm from "@/pages/StoreVisitForm";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import Trips from "@/pages/Trips";
import TripDetail from "@/pages/TripDetail";
import TerritoryMap from "@/pages/TerritoryMap";
import SalesPerformance from "@/pages/SalesPerformance";
import CustomerActivity from "@/pages/CustomerActivity";
import Tasks from "@/pages/Tasks";
import PhotoLibrary from "@/pages/PhotoLibrary";
import SalesImport from "@/pages/SalesImport";
import Reports from "@/pages/Reports";
import UserManagement from "@/pages/UserManagement";
import Settings from "@/pages/Settings";
import TrainingReports from "@/pages/TrainingReports";
import TrainingReportForm from "@/pages/TrainingReportForm";
import TrainingDocs from "@/pages/TrainingDocs";
import TrainingImpact from "@/pages/TrainingImpact";
import Profile from "@/pages/Profile";
import AdminActivity from "@/pages/AdminActivity";
import SalesTargets from "@/pages/SalesTargets";
import Products from "@/pages/Products";
import POSRequests from "@/pages/POSRequests";
import POSCatalogue from "@/pages/POSCatalogue";
import POSReport from "@/pages/POSReport";
import CoverageDashboard from "@/pages/CoverageDashboard";
import AccountPerformance, { AccountsList } from "@/pages/AccountPerformance";
import ProductCoverage from "@/pages/ProductCoverage";
import SalesOpportunities from "@/pages/SalesOpportunities";
import SalesIntelligenceDashboard from "@/pages/SalesIntelligenceDashboard";
import FloorDisplayReport from "@/pages/FloorDisplayReport";
import { Toaster } from "sonner";
import "@/index.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading MLILY CRM…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const Wrapper = user.role === "sales_agent" ? MobileLayout : Layout;
  return <Wrapper>{children}</Wrapper>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/customers" element={<Protected><Customers /></Protected>} />
          <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
          <Route path="/visits" element={<Protected><StoreVisits /></Protected>} />
          <Route path="/visits/new" element={<Protected><StoreVisitForm /></Protected>} />
          <Route path="/visits/:id/edit" element={<Protected><StoreVisitForm /></Protected>} />
          <Route path="/agents" element={<Protected><Agents /></Protected>} />
          <Route path="/agents/:id" element={<Protected><AgentDetail /></Protected>} />
          <Route path="/trips" element={<Protected><Trips /></Protected>} />
          <Route path="/trips/:id" element={<Protected><TripDetail /></Protected>} />
          <Route path="/map" element={<Protected><TerritoryMap /></Protected>} />
          <Route path="/sales" element={<Protected><SalesPerformance /></Protected>} />
          <Route path="/activity" element={<Protected><CustomerActivity /></Protected>} />
          <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
          <Route path="/photos" element={<Protected><PhotoLibrary /></Protected>} />
          <Route path="/import" element={<Protected><SalesImport /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/users" element={<Protected><UserManagement /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/activity-log" element={<Protected><AdminActivity /></Protected>} />
          <Route path="/targets" element={<Protected><SalesTargets /></Protected>} />
          <Route path="/products" element={<Protected><Products /></Protected>} />
          <Route path="/pos-requests" element={<Protected><POSRequests /></Protected>} />
          <Route path="/pos-catalogue" element={<Protected><POSCatalogue /></Protected>} />
          <Route path="/pos-report" element={<Protected><POSReport /></Protected>} />
          <Route path="/coverage-dashboard" element={<Protected><CoverageDashboard /></Protected>} />
          <Route path="/accounts" element={<Protected><AccountsList /></Protected>} />
          <Route path="/accounts/:cid" element={<Protected><AccountPerformance /></Protected>} />
          <Route path="/product-coverage" element={<Protected><ProductCoverage /></Protected>} />
          <Route path="/sales-opportunities" element={<Protected><SalesOpportunities /></Protected>} />
          <Route path="/sales-dashboard" element={<Protected><SalesIntelligenceDashboard /></Protected>} />
          <Route path="/floor-display-report" element={<Protected><FloorDisplayReport /></Protected>} />
          <Route path="/training" element={<Protected><TrainingReports /></Protected>} />
          <Route path="/training/new" element={<Protected><TrainingReportForm /></Protected>} />
          <Route path="/training/impact" element={<Protected><TrainingImpact /></Protected>} />
          <Route path="/docs" element={<Protected><TrainingDocs /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
